import { SPORT_TYPE_VALUES } from '../../pages/SearchPage/SearchV2/constants';
import { headerMenuIDs } from '../Analytics/testIDConfig';
import { buildPathWithQueryParams, constructRedirectPath, getNavigationParameter } from '../../utils/url';
import { paths } from '../../../constants';
import { PopupTypes } from '../../utils/constants';
import { PowerFunnelSurfaces } from '../Analytics/constants';

export const HEADER_FIX_OFFSET = 64;

const buildTestId = (cfg) => `${cfg.component}-${cfg.location}-${cfg.id}`;

export const computeHeaderPosition = ({ scrollY, isResetPosition }) => {
    if (isResetPosition) {
        return { atTop: false, fixHeader: true };
    }
    if (scrollY <= 0) {
        return { atTop: true, fixHeader: false };
    }
    if (scrollY < HEADER_FIX_OFFSET) {
        return { atTop: false, fixHeader: false };
    }
    return { atTop: false, fixHeader: true };
};

export const buildPowerUpgradePopupPayload = (locationPathname) => {
    const payload = {
        popup: PopupTypes.POWER_UPGRADE_POPUP,
        referringSurface: PowerFunnelSurfaces.HEADER,
    };
    if (locationPathname === paths.home) {
        payload.redirectTo = paths.powerPerks;
    }
    return payload;
};

const PREMIUM_CLUB_TIERS = ['Pro', 'Special', 'Free College', 'High School', 'Pro College'];
const PREMIUM_CLUB_ROLES = ['Admin', 'Owner'];

const isPremiumClubMembership = (membership) => (
    PREMIUM_CLUB_TIERS.includes(membership.tierTypeName) &&
    PREMIUM_CLUB_ROLES.includes(membership.roleValue)
);

export const isPremiumClubsOrganiser = (user) => (
    !!user && (user.clubMemberships || []).some(isPremiumClubMembership)
);

export const showHeaderItemsForUser = (user) => (
    !!user && (user.onboardingCompleted || !user.onboardingRequired)
);

export const hasPaidHitPaymentAccountReady = ({ paidHitClubPaymentAccountsLoaded, paidHitClubPaymentAccounts }) => (
    !!paidHitClubPaymentAccountsLoaded && (paidHitClubPaymentAccounts || []).length > 0
);

export const isOnTransparentHomeNav = ({ authUser, locationPathname, hasAnsweredSportPreferencePrompt }) => (
    authUser != null && !hasAnsweredSportPreferencePrompt && locationPathname === paths.home
);

export const getHomeFeedNavStyle = (isTransparent) => ({
    backgroundColor: isTransparent ? 'rgba(0,0,0,0)' : '',
    pointerEvents: isTransparent ? 'none' : '',
});

export const buildNavClassName = ({
    baseClass,
    headerType,
    transparent,
    searchIsExpanded,
    fixHeader,
    showActivateUtrNotification,
    hasUser,
}) => {
    const isTitle = headerType === 'title';
    const classes = [
        'navbar',
        isTitle ? 'title' : '',
        'navbar-expand-lg',
        'fixed-top',
        transparent ? 'navbar-dark' : 'navbar-light',
        isTitle ? 'navbar-title' : '',
        searchIsExpanded ? 'search' : '',
        baseClass,
        fixHeader ? 'fixed-on-bottom-pos' : '',
        transparent ? 'transparent' : '',
        showActivateUtrNotification ? 'activate-utr' : '',
        hasUser ? 'show-power-button' : '',
    ];
    return classes.filter(Boolean).join(' ');
};

export const getSearchUrl = (feedPreferences, type) => {
    const { TENNIS, PICKLEBALL } = SPORT_TYPE_VALUES;
    const hasPreference = feedPreferences && Object.keys(feedPreferences).length > 0;
    if (!hasPreference) {
        return `/search?sportTypes=${TENNIS},${PICKLEBALL}&type=${type}`;
    }
    const { showTennisContent, showPickleballContent } = feedPreferences;
    let sportTypes;
    if (showTennisContent && showPickleballContent) {
        sportTypes = `${TENNIS},${PICKLEBALL}`;
    } else if (showPickleballContent) {
        sportTypes = PICKLEBALL;
    } else {
        sportTypes = TENNIS;
    }
    return `/search?sportTypes=${sportTypes}&type=${type}`;
};

export const buildPlusButtonOptions = ({
    user,
    hasPaidHitClubMembership,
    paidHitClubPaymentAccountsLoaded,
    hasPaidHitPaymentAccount,
    isPaidHitter,
    isPremiumOrganiser,
    isCreateEventEnabled,
}) => {
    const allOptions = [
        {
            id: 'create_play',
            label: 'Create Play',
            imgClass: 'uts uts-play fs20',
            show: !!user,
            testid: buildTestId(headerMenuIDs.createPlay),
        },
        {
            id: 'offer_paid_hit',
            label: 'Offer Paid Hits',
            imgClass: 'icon-dollar-grey d-block-inline',
            show: !!user && (!hasPaidHitClubMembership || (paidHitClubPaymentAccountsLoaded && !hasPaidHitPaymentAccount)),
            testid: buildTestId(headerMenuIDs.offer),
        },
        {
            id: 'create_paid_hit',
            label: 'Create Paid Hit',
            imgClass: 'icon-dollar-grey d-block-inline',
            show: !!user && isPaidHitter,
            testid: buildTestId(headerMenuIDs.createPaidHit),
        },
        {
            id: 'post_a_score',
            label: 'Post score',
            imgClass: 'uts uts-score fs20',
            show: !!user?.claimedPlayer,
            testid: buildTestId(headerMenuIDs.postScore),
        },
        {
            id: 'create_an_event',
            label: 'Create event',
            imgClass: 'uts uts-calendar fs20',
            show: !!user && isPremiumOrganiser && !!isCreateEventEnabled,
            testid: buildTestId(headerMenuIDs.createEvent),
        },
    ];
    return allOptions.filter(option => option.show);
};

export const getMenuOptionPath = (optionId, { canCreateEvent } = {}) => {
    switch (optionId) {
        case 'post_a_score':
            return paths.postScoreSelect;
        case 'create_an_event':
            return canCreateEvent ? paths.createEvent : paths.createTournament;
        case 'create_play':
            return paths.createPlay;
        case 'offer_paid_hit':
            return paths.offerPaidHit;
        case 'create_paid_hit':
            return paths.createPaidHit;
        default:
            return null;
    }
};

const JOIN_PARAM_MAP = {
    next: { source: 'next', target: 'return' },
    redirect: { source: 'redirect', target: 'return' },
    return: { source: 'return', target: 'return' },
};

const LOGIN_PARAM_MAP = {
    return: { source: 'return', target: 'next' },
    redirect: { source: 'redirect', target: 'next' },
    next: { source: 'next', target: 'next' },
};

const resolveAuthRedirectPath = (paramMap, parsed, eventTargetPathname, locationPathname, isReturnParam) => {
    const navigationParam = getNavigationParameter(parsed);
    const mapping = paramMap[navigationParam];
    if (!mapping) {
        return constructRedirectPath(locationPathname, eventTargetPathname, parsed, isReturnParam);
    }
    const next = { ...parsed, [mapping.target]: encodeURIComponent(parsed[mapping.source]) };
    if (mapping.source !== mapping.target) {
        delete next[mapping.source];
    }
    return buildPathWithQueryParams(eventTargetPathname, next);
};

export const getJoinPathName = ({ parsed, eventTargetPathname, locationPathname }) => {
    return resolveAuthRedirectPath(JOIN_PARAM_MAP, parsed, eventTargetPathname, locationPathname, true);
};

export const getLoginPathName = ({ parsed, eventTargetPathname, locationPathname }) => {
    return resolveAuthRedirectPath(LOGIN_PARAM_MAP, parsed, eventTargetPathname, locationPathname, false);
};
